import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Package, Clock, MapPin, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';

export default function Shipping() {
  return (
    <>
      <Helmet>
        <title>Kohaletoimetamine | Tõuks.ee</title>
        <meta name="description" content="Tõuks.ee saatmisinfo: DPD, Omniva ja SmartPost pakiautomaat. Tasuta kohaletoimetamine kogu Eestis. Tarneaeg 1–5 tööpäeva." />
        <link rel="canonical" href="https://xn--tuks-0qa.ee/kohaletoimetamine" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Kodu', item: 'https://xn--tuks-0qa.ee/' },
            { '@type': 'ListItem', position: 2, name: 'Kohaletoimetamine', item: 'https://xn--tuks-0qa.ee/kohaletoimetamine' },
          ],
        })}</script>
      </Helmet>

      <div className="container mx-auto py-12 md:py-20 px-4 md:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Kohaletoimetamine</h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Tasuta saatmine kogu Eestis — vali sobiv pakiautomaat kassas.
          </p>
        </div>

        {/* Free shipping banner */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-10 flex items-center gap-4">
          <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
          <div>
            <p className="font-bold text-green-800 text-lg">Tasuta kohaletoimetamine kõigile tellimustele</p>
            <p className="text-green-700 text-sm mt-1">Saatmiskulu on 0€ — sõltumata tellimuse suurusest.</p>
          </div>
        </div>

        {/* Shipping methods */}
        <h2 className="text-2xl font-bold mb-6 text-center">Saatmisviisid</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              name: 'DPD Pakiautomaat',
              logo: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774461355/DPD-logo_cimemh.svg',
              url: 'https://www.dpd.com/ee/et/',
              logoClass: 'h-10 object-contain',
              time: 'Kuni 7 päeva',
              desc: 'Üle 300 DPD pakiautomaadi üle Eesti. Väga tihe võrk ka väiksemates linnades.',
              price: 'Tasuta',
            },
            {
              name: 'Omniva Pakiautomaat',
              logo: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774461355/Omniva-logo_eotudv.svg',
              url: 'https://www.omniva.ee/',
              logoClass: 'h-12 object-contain',
              time: 'Kuni 7 päeva',
              desc: 'Omniva pakiautomaat postkontorites ja kauplustes. Tihe võrk üle Eesti.',
              price: 'Tasuta',
            },
            {
              name: 'SmartPost Pakiautomaat',
              logo: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774461354/Logo_SmartPosti_hmbodj.avif',
              url: 'https://www.smartposti.ee/',
              logoClass: 'h-8 object-contain',
              time: 'Kuni 7 päeva',
              desc: 'SmartPost pakiautomaat Prisma, Selver ja Maxima kauplustes.',
              price: 'Tasuta',
            },
          ].map((method) => (
            <div key={method.name} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
              <a href={method.url} target="_blank" rel="noopener noreferrer" className="h-12 flex items-center justify-center mb-4">
                <img
                  src={method.logo}
                  alt={method.name}
                  className={method.logoClass}
                  onError={(e) => {
                    const t = e.target as HTMLImageElement;
                    t.style.display = 'none';
                  }}
                />
              </a>
              <h3 className="font-bold text-gray-800 mb-2 text-center">{method.name}</h3>
              <p className="text-gray-500 text-sm flex-1 mb-4 text-center">{method.desc}</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{method.time}</span>
                </div>
                <span className="text-green-600 font-bold">{method.price}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Delivery info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-10">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Package className="w-6 h-6 text-[#4FC1E9]" />
            Tarneinfo
          </h2>
          <div className="space-y-4">
            {[
              {
                icon: <Clock className="w-5 h-5 text-[#4FC1E9]" />,
                title: 'Pakendamise aeg',
                desc: 'Laos olevad tooted pakendame ja saadame 1–2 tööpäeva jooksul. Tellitavad tooted jõuavad lattu 5–14 tööpäevaga.',
              },
              {
                icon: <MapPin className="w-5 h-5 text-[#4FC1E9]" />,
                title: 'Tarnepiirkond',
                desc: 'Saadame kogu Eestis. Rahvusvaheline kohaletoimetamine hetkel ei ole saadaval.',
              },
              {
                icon: <CheckCircle className="w-5 h-5 text-[#4FC1E9]" />,
                title: 'Jälgimine',
                desc: 'Pärast saatmist saad e-postile jälgimiskoodi, millega saad saadetise asukohta reaalajas jälgida.',
              },
              {
                icon: <AlertCircle className="w-5 h-5 text-[#4FC1E9]" />,
                title: 'Toote puudumine',
                desc: 'Kui tellitud toodet pole võimalik tarnida, teavitame sind esimesel võimalusel ja tagastame makstud summa 14 päeva jooksul.',
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 py-4 border-b border-gray-50 last:border-0">
                <div className="bg-[#4FC1E9]/10 rounded-lg p-2 flex-shrink-0">{item.icon}</div>
                <div>
                  <p className="font-semibold text-gray-800">{item.title}</p>
                  <p className="text-gray-500 text-sm mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { to: '/garantii-ja-tagastamine', label: 'Tagastamine ja garantii', sub: '14-päevane tagastamisõigus' },
            { to: '/kontakt', label: 'Küsi saatmise kohta', sub: 'Vastame 1 tööpäeva jooksul' },
          ].map(({ to, label, sub }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center justify-between bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-[#4FC1E9] transition-colors group"
            >
              <div>
                <p className="font-semibold text-gray-800 group-hover:text-[#4FC1E9] transition-colors">{label}</p>
                <p className="text-gray-500 text-sm mt-1">{sub}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#4FC1E9] transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
